import React from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { TrackPoint, Segment } from '../types';

interface SpeedChartProps {
  data: TrackPoint[];
  segments: Segment[];
  selectedSegment: Segment | null;
}

export const SpeedChart: React.FC<SpeedChartProps> = ({ data, segments, selectedSegment }) => {
  
  // Helper for formatting seconds to mm:ss
  const formatDuration = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const { formattedData, splitPoints } = React.useMemo(() => {
    let pointsToChart: { speed: number; hr?: number; timeAxis: number; rawTime: Date; }[] = [];
    let splits: { x: number; label: string }[] = []; 

    if (selectedSegment) {
      // SINGLE SEGMENT VIEW
      const startTime = selectedSegment.points[0].time.getTime();
      pointsToChart = selectedSegment.points.map(pt => ({
        speed: pt.speed,
        hr: pt.heartRate,
        timeAxis: (pt.time.getTime() - startTime) / 1000, // Seconds from start of run
        rawTime: pt.time
      }));
    } else {
      // ALL SEGMENTS VIEW: Concatenate active time
      let virtualTimeSeconds = 0;
      
      segments.forEach((seg, segIndex) => {
        
        if (segIndex > 0) {
           // Calculate the gap duration (Trimmed time)
           const prevSeg = segments[segIndex - 1];
           const prevEndTime = prevSeg.points[prevSeg.points.length - 1].time.getTime();
           const currStartTime = seg.points[0].time.getTime();
           const gapSeconds = (currStartTime - prevEndTime) / 1000;
           
           // Store split point at current virtual time
           splits.push({ 
             x: virtualTimeSeconds, 
             label: `+${formatDuration(gapSeconds)}` 
           });
           
           // Add a null point to break the line cleanly if desired, 
           // or just connect them to show flow. We'll connect them but separate via axis logic.
           // To prevent a vertical line jump if speeds differ wildly, we just proceed.
        }

        const segStartTime = seg.points[0].time.getTime();
        
        seg.points.forEach(pt => {
          const localOffset = (pt.time.getTime() - segStartTime) / 1000;
          pointsToChart.push({
            speed: pt.speed,
            hr: pt.heartRate,
            timeAxis: virtualTimeSeconds + localOffset,
            rawTime: pt.time
          });
        });
        
        // Increment virtual time by this segment's duration for the next one
        virtualTimeSeconds += seg.stats.duration;
      });
    }

    // Format for display
    const formatted = pointsToChart.map(pt => ({
      ...pt,
      timeDisplay: formatDuration(pt.timeAxis), // X-axis label
    }));

    return { formattedData: formatted, splitPoints: splits };

  }, [data, segments, selectedSegment]);

  const hasHeartRate = formattedData.some(d => d.hr !== undefined && d.hr > 0);

  return (
    <div className="w-full h-80 bg-slate-900 p-4 rounded-2xl border border-slate-800 flex flex-col">
      <div className="flex justify-between items-center mb-4 pl-2">
        <h3 className="text-lg font-semibold text-slate-200">
          {selectedSegment ? `Run #${selectedSegment.id} Analysis` : 'Combined Runs Profile'}
        </h3>
        <div className="flex items-center gap-3">
           {!selectedSegment && (
             <div className="flex items-center space-x-2 text-xs px-2 py-1 bg-pink-950/20 text-pink-400 rounded border border-pink-900/30">
               <div className="h-3 w-0 border-l border-dashed border-pink-500"></div>
               <span>Pause Time</span>
             </div>
           )}
           <span className="text-xs px-2 py-1 bg-cyan-900/30 text-cyan-400 rounded border border-cyan-900/50">
             {selectedSegment ? 'Zoomed View' : 'Action Only (Time Based)'}
           </span>
        </div>
      </div>
      <div className="flex-1 min-h-0">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={formattedData}>
            <defs>
              <linearGradient id="colorSpeed" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#06b6d4" stopOpacity={0.3}/>
                <stop offset="95%" stopColor="#06b6d4" stopOpacity={0}/>
              </linearGradient>
              <linearGradient id="colorHr" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#ef4444" stopOpacity={0.3}/>
                <stop offset="95%" stopColor="#ef4444" stopOpacity={0}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" vertical={false} />
            <XAxis 
              dataKey="timeAxis" 
              stroke="#64748b" 
              tick={{fontSize: 12}}
              tickFormatter={(val) => formatDuration(val)}
              minTickGap={40}
              type="number"
              domain={['dataMin', 'dataMax']}
              label={{ value: 'Active Time (mm:ss)', position: 'insideBottomRight', offset: -5, fill: '#64748b', fontSize: 10 }}
            />
            <YAxis 
              yAxisId="left"
              stroke="#06b6d4" 
              tick={{fontSize: 12}}
              tickFormatter={(value) => `${value}`}
              label={{ value: 'Speed (km/h)', angle: -90, position: 'insideLeft', fill: '#06b6d4', fontSize: 10 }}
              domain={[0, 'auto']}
            />
            {hasHeartRate && (
              <YAxis 
                yAxisId="right"
                orientation="right"
                stroke="#ef4444"
                tick={{fontSize: 12}}
                domain={['dataMin - 10', 'auto']}
                label={{ value: 'HR (bpm)', angle: 90, position: 'insideRight', fill: '#ef4444', fontSize: 10 }}
              />
            )}
            <Tooltip 
              contentStyle={{ backgroundColor: '#0f172a', borderColor: '#334155', color: '#f1f5f9' }}
              formatter={(value: any, name: string) => {
                if (name === 'speed') return [`${Number(value).toFixed(1)} km/h`, 'Speed'];
                if (name === 'hr') return [`${value} bpm`, 'Heart Rate'];
                return [value, name];
              }}
              labelFormatter={(label) => `Time: ${formatDuration(label)}`}
            />
            
            {/* Render Reference Lines for Splits in Combined View */}
            {!selectedSegment && splitPoints.map((pt, i) => (
              <ReferenceLine 
                key={i} 
                x={pt.x} 
                stroke="#ec4899" 
                strokeDasharray="3 3" 
                yAxisId="left"
                strokeWidth={1.5}
                label={{ 
                  value: pt.label, 
                  position: 'insideBottomLeft', 
                  angle: -90, // Reads bottom-to-top
                  fill: '#ec4899', 
                  fontSize: 10, 
                  fontWeight: 'bold',
                  offset: 10,
                }}
              />
            ))}

            <Area 
              yAxisId="left"
              type="monotone" 
              dataKey="speed" 
              stroke="#06b6d4" 
              fillOpacity={1} 
              fill="url(#colorSpeed)" 
              strokeWidth={2}
              name="speed"
              isAnimationActive={false}
              connectNulls={false}
            />
            {hasHeartRate && (
               <Area 
                 yAxisId="right"
                 type="monotone" 
                 dataKey="hr" 
                 stroke="#ef4444" 
                 fillOpacity={1} 
                 fill="url(#colorHr)" 
                 strokeWidth={2}
                 name="hr"
                 isAnimationActive={false}
                 connectNulls={false}
               />
            )}
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};